import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { LockClosedIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { useAuth } from '@bri/shared-components'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { getErrorMessage } from '@/lib/utils'
import toast from 'react-hot-toast'

const hasValidPasswordShape = (password: string) =>
  password.length >= 8 && /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/.test(password)

export default function ForgotPasswordPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { requestPasswordReset, confirmPasswordReset } = useAuth()

  const initialEmail = useMemo(() => location.state?.email || '', [location.state])

  const [email, setEmail] = useState(initialEmail)
  const [verificationCode, setVerificationCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [hasRequestedCode, setHasRequestedCode] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResending, setIsResending] = useState(false)

  const validateNewPassword = () => {
    if (!newPassword) {
      toast.error('Please enter a new password')
      return false
    }

    if (!hasValidPasswordShape(newPassword)) {
      toast.error('Password must be at least 8 characters with uppercase, lowercase, number, and special character')
      return false
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return false
    }

    return true
  }

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      toast.error('Please enter your email address')
      return
    }

    setIsSubmitting(true)

    try {
      await requestPasswordReset(email)
      setHasRequestedCode(true)
      toast.success('Password reset code sent! Check your email.')
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!verificationCode.trim()) {
      toast.error('Please enter the reset code')
      return
    }

    if (!validateNewPassword()) {
      return
    }

    setIsSubmitting(true)

    try {
      await confirmPasswordReset(email, verificationCode, newPassword)
      toast.success('Password reset successfully! You can now sign in.')
      navigate('/auth/login', { state: { email } })
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResendCode = async () => {
    if (!email.trim()) {
      toast.error('Please enter your email address')
      return
    }

    setIsResending(true)

    try {
      await requestPasswordReset(email)
      toast.success('Reset code sent again!')
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
            <LockClosedIcon className="h-8 w-8 text-primary" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-foreground">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            {hasRequestedCode
              ? 'Enter the reset code from your email and choose a new password.'
              : 'We’ll email you a reset code so you can choose a new password.'}
          </p>
        </div>

        {!hasRequestedCode ? (
          <form className="mt-8 space-y-6" onSubmit={handleRequestCode}>
            <div>
              <label htmlFor="email" className="form-label">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="form-input"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Sending code...
                </div>
              ) : (
                'Send reset code'
              )}
            </button>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="form-label">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="verificationCode" className="form-label">
                  Reset code
                </label>
                <input
                  id="verificationCode"
                  name="verificationCode"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  className="form-input text-center text-lg tracking-widest"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={isSubmitting}
                  maxLength={6}
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="form-label">
                  New password
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="form-input"
                  placeholder="Create a new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isSubmitting}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Must be at least 8 characters with uppercase, lowercase, number, and special character
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm new password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="form-input"
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || verificationCode.length !== 6}
              className="btn-primary w-full"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Resetting password...
                </div>
              ) : (
                'Reset password'
              )}
            </button>
          </form>
        )}

        <div className="bg-card rounded-lg border p-6 space-y-4">
          <div className="flex items-start space-x-3">
            <CheckCircleIcon className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              Check your spam folder if you don’t see the reset email
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircleIcon className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              The reset code will expire if not used within Cognito’s allowed window
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {hasRequestedCode && (
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Didn’t receive the email?{' '}
                <button
                  className="font-medium text-primary hover:text-primary/80 disabled:opacity-50"
                  onClick={handleResendCode}
                  disabled={isResending}
                >
                  {isResending ? 'Sending...' : 'Resend reset code'}
                </button>
              </p>
            </div>
          )}

          <div className="text-center">
            <Link to="/auth/login" state={{ email }} className="text-sm font-medium text-primary hover:text-primary/80">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
